"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight } from "lucide-react";
import { useCategories } from "@/app/hooks/use-categories";
import { useEffect, useState } from "react";
import { getCanvasProductForOrder } from "@/app/lib/api/canvas-products";

interface Breadcrumb {
  label: string;
  path: string;
  isLast: boolean;
}

export function Breadcrumb() {
  const pathname = usePathname();
  const { menuItems } = useCategories();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  useEffect(() => {
    if (pathname === "/") {
      setBreadcrumbs([]);
      return;
    }

    const generateBreadcrumb = async () => {
      const paths = pathname.split("/").filter(Boolean);
      const breadcrumbs: Breadcrumb[] = [];
      let currentPath = "";
      const findLabels = (path: string) => {
        const menuItem = menuItems.find((item) => {
          if (item.href === `/${path}`) return true;
          if (item.subItems) {
            return item.subItems.some((subItem) =>
              subItem.href.includes(`/${path}`)
            );
          }
          return false;
        });

        if (menuItem) {
          if (menuItem.href === `/${path}`) return menuItem.label;
          const subItem = menuItem.subItems?.find((sub) =>
            sub.href.includes(`/${path}`)
          );
          if (subItem) return subItem.label;
        }

        return path
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      };

      if (paths.length > 0 && paths[0] === "siparis") {
        const productId = paths[paths.length - 1].split("-").pop();
        if (productId) {
          if (paths[1] === "kanvas") {
            const product = await getCanvasProductForOrder(productId);
            if (product) {
              breadcrumbs.push({
                label: product.title,
                path: `/siparis/kanvas/${product.url}-${product.id}`,
                isLast: true,
              });
            }
          }
          return breadcrumbs;
        }
      }
      paths.forEach((path, index) => {
        currentPath += `/${path}`;
        const label = findLabels(path);

        breadcrumbs.push({
          label,
          path: currentPath,
          isLast: index === paths.length - 1,
        });
      });

      return breadcrumbs;
    };

    const fetchData = async () => {
      const breadcrumbs = await generateBreadcrumb();
      setBreadcrumbs(breadcrumbs);
    };
    fetchData();
  }, [pathname, menuItems]);

  return (
    <>
      <div className="absolute w-full lg:top-[146px] top-[100px]">
        <div className="container mx-auto px-4">
          <div className="flex items-center py-4 text-sm">
            <Link href="/" className="text-gray-600 hover:text-gray-900">
              Anasayfa
            </Link>
            {breadcrumbs.length > 0 &&
              breadcrumbs.map((crumb) => (
                <div key={crumb.path} className="flex items-center">
                  <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
                  {crumb.isLast ? (
                    <span className="text-gray-900 font-medium">
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      href={crumb.path}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="opacity-0 pointer-events-none">
        <div className="container mx-auto px-4">
          <div className="flex items-center py-4 text-sm">
            <Link href="/" className="text-gray-600 hover:text-gray-900">
              Anasayfa
            </Link>
            {breadcrumbs.length > 0 &&
              breadcrumbs.map((crumb) => (
                <div key={crumb.path} className="flex items-center">
                  <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
                  {crumb.isLast ? (
                    <span className="text-gray-900 font-medium">
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      href={crumb.path}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
}